import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPlay, FaPause, FaArrowLeft, FaForward, FaBackward } from 'react-icons/fa'; // Added FaForward, FaBackward
import axios from 'axios';
import { QUIZMASTER_API } from '../config';
import '../styles/styles.css';
import FloatingChat from './FloatingChat';

const PodcastPage = ({ accessToken, supabaseRefreshToken, onSignIn }) => {
    const [podcast, setPodcast] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
    const [audioGenerationProgress, setAudioGenerationProgress] = useState(0);
    const [playbackSpeed, setPlaybackSpeed] = useState(1); // New state for speed

    const location = useLocation();
    const navigate = useNavigate();
    const selectedPodcast = location.state?.selectedPodcast;
    const document = location.state?.selectedDocument;
    const audioRef = useRef(new Audio());

    const handleReturnToPodcasts = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setIsPlaying(false);
        setIsPaused(false);
        if (document) {
            navigate('/podcast-list', { state: { selectedDocument: document } });
        } else {
            navigate('/profile');
        }
    }, [document, navigate]);

    // New: Skip forward 10 seconds
    const skipForward = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = Math.min(audioRef.current.duration, audioRef.current.currentTime + 10);
        }
    };

    // New: Skip backward 10 seconds
    const skipBackward = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
        }
    };

    // New: Handle speed changes
    const handleSpeedChange = (e) => {
        const speed = parseFloat(e.target.value);
        setPlaybackSpeed(speed);
        if (audioRef.current) {
            audioRef.current.playbackRate = speed;
        }
    };

    const playAudio = useCallback(() => {
        if (!podcast || !podcast.audio_url) return;
        if (audioRef.current) {
            audioRef.current.pause();
        }
        audioRef.current.src = podcast.audio_url;
        // Ensure the playback rate is reapplied when the source changes
        audioRef.current.playbackRate = playbackSpeed;
        audioRef.current.onended = () => {
            setIsPlaying(false);
            setIsPaused(false);
        };
        audioRef.current.play()
            .then(() => {
                setIsPlaying(true);
                setIsPaused(false);
                setError(null);
            })
            .catch(err => {
                console.error('Playback error:', err);
                setIsPlaying(false);
            });
    }, [podcast, playbackSpeed]);

    const generateAudioForPodcast = useCallback(async (podcastData) => {
        if (!podcastData.segments && !podcastData.content) return;
        if (podcastData.audio_url) return;
        setIsGeneratingAudio(true);
        setAudioGenerationProgress(0);
        try {
            const params = new URLSearchParams({
                document_id: podcastData.document_id.toString(),
                duration_minutes: podcastData.duration_estimate_minutes?.toString() || '1',
                enable_ocr: 'true',
                generate_audio: 'true'
            });
            let response = await axios.post(
                `${QUIZMASTER_API}/generate-podcast/?${params.toString()}`,
                {},
                { headers: { 'Authorization': `Bearer ${accessToken}` } }
            );
            if (response.data.podcast_saved) {
                const audioUrlResponse = await fetch(
                    `${QUIZMASTER_API}/get-podcast-audio-url/?podcast_id=${response.data.podcast_id || podcastData.id}&expires_in=3600`,
                    { headers: { 'Authorization': `Bearer ${accessToken}` } }
                );
                if (audioUrlResponse.ok) {
                    const audioUrlData = await audioUrlResponse.json();
                    setPodcast(prev => ({ ...prev, audio_url: audioUrlData.audio_url }));
                    setAudioGenerationProgress(100);
                }
            }
        } catch (err) {
            console.error('Error generating audio:', err);
            setError('Failed to generate audio.');
        } finally {
            setIsGeneratingAudio(false);
        }
    }, [accessToken]);

    useEffect(() => {
        const initialize = async () => {
            if (selectedPodcast) {
                let podcastData = { ...selectedPodcast };
                if (selectedPodcast.audio_file_path && !selectedPodcast.audio_url) {
                    try {
                        const res = await fetch(
                            `${QUIZMASTER_API}/get-podcast-audio-url/?podcast_id=${selectedPodcast.id}&expires_in=3600`,
                            { headers: { 'Authorization': `Bearer ${accessToken}` } }
                        );
                        if (res.ok) {
                            const data = await res.json();
                            podcastData.audio_url = data.audio_url;
                        }
                    } catch (e) { console.error(e); }
                }
                setPodcast(podcastData);
                setLoading(false);
                if (!podcastData.audio_url) generateAudioForPodcast(podcastData);
            } else {
                setError('No podcast selected.');
                setLoading(false);
            }
        };
        initialize();
    }, [selectedPodcast, accessToken, generateAudioForPodcast]);

    const togglePlayback = () => {
        if (isPlaying && !isPaused) {
            audioRef.current.pause();
            setIsPaused(true);
        } else if (isPaused) {
            audioRef.current.play();
            setIsPaused(false);
        } else {
            if (podcast?.audio_url) {
                playAudio();
            }
        }
    };

    const replaceUnicodeEscapes = (str) => {
        if (typeof str !== 'string') return str;
        return str
            .replace(/u2019/g, "'")
            .replace(/u2013/g, '–')
            .replace(/u2014/g, '—')
            .replace(/u2018/g, '‘')
            .replace(/u201C/g, '“')
            .replace(/u201D/g, '”')
            .replace(/u2026/g, '…')
            .replace(/\\"/g, '"')
            .replace(/\\[{}[\]]/g, '')
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t');
    };

    const renderTranscript = () => {
        const rawData = podcast.segments || podcast.content;

        if (!rawData) {
            return <p className="text-gray-500 italic">No transcript available.</p>;
        }

        let segments = [];
        if (Array.isArray(rawData)) {
            segments = rawData;
        } else if (typeof rawData === 'string') {
            try {
                const parsed = JSON.parse(rawData);
                if (Array.isArray(parsed)) segments = parsed;
            } catch (e) {
                return <p className="podcast-raw-text">{rawData}</p>;
            }
        } else {
            return <p className="podcast-raw-text">{JSON.stringify(rawData)}</p>;
        }

        const cleanedSegments = segments.map((seg) => {
            const speaker = (seg.speaker || 'Unknown').trim();
            const text = (seg.text || '').trim()
                .replace(/\\u2013/g, '–')
                .replace(/\\u2019/g, "'")
                .replace(/\\"/g, '"');
            return { speaker, text };
        }).filter(seg => seg.text);

        if (cleanedSegments.length === 0) {
            return <p className="text-gray-500 italic">No valid transcript segments found.</p>;
        }

        return (
            <div className="transcript-container">
                {cleanedSegments.map((seg, index) => (
                    <div key={index} className="transcript-segment">
                        <div className="transcript-speaker">
                            {seg.speaker}
                        </div>
                        <div className="transcript-text">
                            {seg.text}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    if (loading) return <div className="loading-container"><div className="spinner"></div><p>Loading...</p></div>;
    if (error || !podcast) return <div className="error-container"><p>{error}</p><button onClick={handleReturnToPodcasts}>Back</button></div>;

    return (
        <div className="scroll-container">
            <div className="podcast-page-wrapper">
                <div className="podcast-header">
                    <button className="button button--ghost podcast-back-button" onClick={handleReturnToPodcasts}>
                        <FaArrowLeft /> Back to Podcasts
                    </button>
                    <h1 className="podcast-title">{podcast.title}</h1>
                    <p className="podcast-meta">Duration: {podcast.duration_estimate_minutes} minutes</p>
                </div>

                <div className="podcast-controls">
                    <div className="playback-controls">
                        {isGeneratingAudio ? (
                            <div className="audio-generating">
                                <div className="spinner spinner--inline"></div>
                                <p>Generating audio... {audioGenerationProgress > 0 ? `${Math.round(audioGenerationProgress)}%` : ''}</p>
                            </div>
                        ) : (
                            <div className="control-bar" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                {/* Rewind 10s */}
                                <button className="button skip-button" onClick={skipBackward} disabled={!podcast.audio_url}>
                                    <FaBackward style={{ marginRight: '4px' }} /> 10s
                                </button>

                                {/* Play/Pause */}
                                <button className="button podcast-play-button" onClick={togglePlayback} disabled={!podcast.audio_url}>
                                    {isPlaying && !isPaused ? <FaPause style={{ marginRight: '8px' }} /> : <FaPlay style={{ marginRight: '8px' }} />}
                                    {isPlaying ? (isPaused ? ' Resume' : ' Pause') : ' Play Episode'}
                                </button>

                                {/* Forward 10s */}
                                <button className="button skip-button" onClick={skipForward} disabled={!podcast.audio_url}>
                                    10s <FaForward style={{ marginLeft: '4px' }} />
                                </button>

                                {/* Speed Selector */}
                                <select
                                    className="speed-selector"
                                    value={playbackSpeed}
                                    onChange={handleSpeedChange}
                                    style={{ marginLeft: '8px', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}
                                >
                                    <option value="0.5">0.5x</option>
                                    <option value="0.8">0.8x</option>
                                    <option value="1">1.0x</option>
                                    <option value="1.2">1.2x</option>
                                    <option value="1.5">1.5x</option>
                                    <option value="2">2.0x</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                <div className="podcast-content">
                    <h2 className="podcast-transcript-header">Transcript</h2>
                    {renderTranscript()}
                </div>
            </div>
            {document && (
                <FloatingChat
                    documentId={document.id}
                    filename={document.filename}
                    accessToken={accessToken}
                />
            )}
        </div>
    );
};

export default PodcastPage;